<template>
  <modal-wrap
    :id="modalName"
    :name="modalName"
    header-class="border-0 label-errors-header"
    body-class="label-errors-modal"
    size="lg"
  >
    <template #modal-title>
      <h4 class="mb-0">Some shipping labels weren’t generated</h4>
    </template>
    <template #default>
      <div>
        <easy-data-table
          ref="dataTable"
          v-model:server-options="serverOptions"
          table-class-name="vue3-easy-data-table table-shadow"
          :server-items-length="serverItemsLength"
          :loading="loading"
          :headers="headers"
          :items="items"
          :prevent-context-menu-row="false"
          :fixed-header="false"
          no-hover
          alternating
          hide-footer
        >
          <template #loading>
            <b-spinner class="mt-5" variant="success" />
          </template>
          <template #no-data>
            <div style="text-align: center">No missing labels</div>
          </template>
          <template #item="{ column, item }: { column: string; item: Item }">
            <span v-if="column === 'reference'">{{ item.reference }}</span>
            <span v-else-if="column === 'recipientAddress'">
              {{ item.recipientAddress.addressLine }}, {{ item.recipientAddress.city }},
              {{ item.recipientAddress.postalCode }}, {{ item.recipientAddress.countryCode }}
              <div v-if="isReCreatable(item)" class="error-text">Service temporary unavailable.</div>
              <div v-else-if="isValidationFailed(item)" class="error-text">Address validation failed.</div>
              <div v-else class="error-text">Optimization failed. Fallback carrier cannot deliver to this address.</div>
              <div v-if="item.shippingError" class="error-text">{{ formatShippingError(item.shippingError) }}</div>
            </span>
            <span v-else-if="column === 'actions'">
              <button v-if="isReCreatable(item)" class="btn btn-green" @click="recreateLabel(item)">
                <b-spinner v-if="isState(item.id, 'loading')" variant="light" small />
                <mdicon v-else-if="isState(item.id, 'success')" name="CheckCircleOutline" variant="light" size="20" />
                <mdicon v-else-if="isState(item.id, 'error')" name="ReloadAlert" variant="light" size="20" />
                <mdicon v-else name="Reload" variant="light" size="20" />
                <span class="ml-2">Re-create label</span>
              </button>
            </span>
          </template>
        </easy-data-table>

        <TablePagination
          v-model:server-options-ref="serverOptions"
          v-model:data-table-ref="dataTable"
          :server-items-length="serverItemsLength"
        />
      </div>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { Item, LabelItemStatus } from '@/types/Models/Item'
import { BSpinner } from 'bootstrap-vue-next'
import { storeToRefs } from 'pinia'
import { defineComponent, watch, ref, computed, type ComputedRef, type Ref } from 'vue'
import { LABEL_ERRORS_MODAL } from '@/const/ModalTypes'
import { formatShippingError } from '@/services/Helpers'
import { registerModals } from '@/services/Modal'
import { useAssignmentStore } from '@/store/assignment'
import { useAssignmentSummaryStore } from '@/store/assignmentSummary'
import { useItemStore } from '@/store/item'
import { useVueNextModalStore } from '@/store/vueNextModal'
import EasyDataTable, { type Header, type ServerOptions } from 'vue3-easy-data-table'
import ModalWrap from '@/views/Modals/ModalWrap.vue'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'

export default defineComponent({
  name: 'LabelErrorsModal',
  components: { BSpinner, TablePagination, EasyDataTable, ModalWrap },
  setup() {
    const dataTable = ref()
    const itemStore = useItemStore()
    const modalStore = useVueNextModalStore()
    const assignmentStore = useAssignmentStore()
    const assignmentSummaryStore = useAssignmentSummaryStore()
    const modalName = LABEL_ERRORS_MODAL
    const statusMap: Ref<Record<string, LabelItemStatus>> = ref({})
    const serverOptions: Ref<ServerOptions> = ref({ page: 1, rowsPerPage: 25 })

    const modalOpened: ComputedRef<boolean> = computed(() => modalStore.isModalVisible(modalName))
    const { getMissingLabelItems, getMissingLabelItemsCount, getMissingLabelItemsLoading } = storeToRefs(itemStore)

    const loadMisingLabels = () => {
      if (modalOpened.value && assignmentSummaryStore.assignment?.id) {
        itemStore.fetchMissingLabelItems(assignmentSummaryStore.assignment.id, serverOptions.value)
      }
    }
    const unavailable = ['500', '502', '503', '504', 'timed out', 'timeout', 'unavailable', 'too many attempts']
    const validation = ['422', '400', 'validation failed', 'invalid address', 'creation failed', 'data was invalid']
    const isReCreatable = (item: Item): boolean =>
      !!item.shippingError && unavailable.some((substring) => item.shippingError?.toLowerCase().includes(substring))
    const isValidationFailed = (item: Item): boolean =>
      !!item.shippingError && validation.some((substring) => item.shippingError?.toLowerCase().includes(substring))
    const isState = (id: string, state: LabelItemStatus): boolean => (statusMap.value[id] ?? 'idle') === state
    const setState = (id: string, state: LabelItemStatus) => (statusMap.value[id] = state)
    const recreateLabel = async (item: Item) => {
      setState(item.id, 'loading')
      try {
        await itemStore.recreateLabel(item.id)
        setState(item.id, 'success')
      } catch {
        setState(item.id, 'error')
      } finally {
        setTimeout(() => {
          setState(item.id, 'idle')
          loadMisingLabels()
        }, 3000)
        assignmentStore.refreshItems()
      }
    }

    const showActions: ComputedRef<boolean> = computed(() => (getMissingLabelItems.value ?? []).some(isReCreatable))
    const headers: ComputedRef<Header[]> = computed(() => [
      { text: 'Reference', value: 'reference' },
      { text: 'Address', value: 'recipientAddress' },
      ...(showActions.value ? [{ text: 'Actions', value: 'actions' }] : []),
    ])

    watch(modalOpened, loadMisingLabels, { immediate: true })
    watch(serverOptions, loadMisingLabels)

    registerModals(modalName)

    return {
      headers,
      dataTable,
      modalName,
      serverOptions,
      items: getMissingLabelItems as Ref<Item[]>,
      loading: getMissingLabelItemsLoading,
      serverItemsLength: getMissingLabelItemsCount,
      isState,
      recreateLabel,
      isReCreatable,
      isValidationFailed,
      formatShippingError,
    }
  },
})
</script>
